// OutOfArea.js
// Shown to users (customers or providers) who sign up from outside our service area.
// Loads their city/state/role from their Firestore profile, checks if they have
// already submitted an expansion request, and either shows the request form
// or the thank-you state immediately.
// Route: /out-of-area (public route, auth checked internally)

import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
} from 'firebase/firestore';

function OutOfArea() {
  const navigate = useNavigate();

  // ===== AUTH / PROFILE STATE =====
  const [userData, setUserData] = useState(null);   // profile from Firestore
  const [currentUser, setCurrentUser] = useState(null); // Firebase Auth user
  const [loadingProfile, setLoadingProfile] = useState(true);

  // ===== EXPANSION REQUEST STATE =====
  const [alreadyRequested, setAlreadyRequested] = useState(false); // existing doc found
  const [showForm, setShowForm] = useState(false);   // show the request form
  const [city, setCity] = useState('');              // pre-filled from profile
  const [state, setState] = useState('');            // pre-filled from profile
  const [message, setMessage] = useState('');        // optional free-text
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Page fade-in on mount
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setTimeout(() => setVisible(true), 50);
  }, []);

  // ===== LOAD PROFILE AND CHECK FOR EXISTING REQUEST =====
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setCurrentUser(user);

      if (!user) {
        // Not logged in — still show the page but without pre-fill
        setLoadingProfile(false);
        return;
      }

      try {
        // Load the user's Firestore profile (works for both roles)
        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (userSnap.exists()) {
          const data = userSnap.data();
          setUserData(data);

          // Pre-fill city/state from their profile
          setCity(data.city || '');
          setState(data.state || '');
        }

        // Check if this user has already submitted an expansion request
        const reqQuery = query(
          collection(db, 'expansionRequests'),
          where('userId', '==', user.uid)
        );
        const reqSnap = await getDocs(reqQuery);
        if (!reqSnap.empty) {
          setAlreadyRequested(true);
        }
      } catch (err) {
        console.error('Error loading profile or expansion request:', err);
      } finally {
        setLoadingProfile(false);
      }
    });

    return () => unsubscribe();
  }, []);

  // ===== SUBMIT EXPANSION REQUEST =====
  const handleSubmit = async () => {
    const user = auth.currentUser;

    setSubmitting(true);
    setSubmitError('');

    try {
      // Write to expansionRequests collection in Firestore
      await addDoc(collection(db, 'expansionRequests'), {
        userId: user ? user.uid : 'anonymous',
        email: user ? user.email : '',
        city: city.trim(),
        state: state.trim(),
        message: message.trim(),
        requestedAt: new Date().toISOString(),
      });

      // Show thank-you state
      setSubmitted(true);
    } catch (err) {
      console.error('Expansion request error:', err);
      setSubmitError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ===== LOG OUT =====
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error('Sign out error:', err);
    }
    navigate('/');
  };

  // ===== HELPERS =====
  // Capitalize a string for display (city/state may be stored lowercase)
  const capitalize = (str) => {
    if (!str) return '';
    return str.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  // Human-readable location label — e.g. "Pittsburgh, PA"
  const locationLabel = city && state
    ? `${capitalize(city)}, ${state.toUpperCase()}`
    : city ? capitalize(city) : null;

  // ===== LOADING SPINNER =====
  if (loadingProfile) {
    return (
      <div className="auth-gate-loading">
        <div className="loading-spinner" />
      </div>
    );
  }

  // ===== RENDER =====
  // showThankYou is true when they already had a request OR just submitted one
  const showThankYou = alreadyRequested || submitted;

  return (
    <div
      className="out-of-area-page"
      style={{ opacity: visible ? 1 : 0, transition: 'opacity 0.4s ease' }}
    >
      {/* ===== CARD ===== */}
      <div className="out-of-area-card">

        {/* ===== THANK YOU STATE ===== */}
        {showThankYou ? (
          <div className="out-of-area-thankyou">

            {/* Success icon */}
            <div className="out-of-area-success-icon">✓</div>

            <h1 className="out-of-area-title">Request received!</h1>

            <p className="out-of-area-body">
              Thank you for your interest! We have recorded your request
              {locationLabel ? ` for ${locationLabel}` : ''}. We will notify
              you by email when service becomes available in your area.
            </p>

            {/* Go to Homepage — always shown */}
            <button
              className="btn-primary out-of-area-btn"
              onClick={() => navigate('/')}
            >
              Go to Homepage
            </button>

            {/* Log Out — only shown when signed in */}
            {currentUser && (
              <button
                className="btn-link"
                style={{ display: 'block', textAlign: 'center', marginTop: '12px' }}
                onClick={handleLogout}
              >
                Log Out
              </button>
            )}

          </div>

        ) : (
          <>
            {/* ===== DEFAULT STATE — warm message + request button ===== */}

            {/* Icon */}
            <div className="out-of-area-icon">🌿</div>

            <h1 className="out-of-area-title">We're not in your area yet</h1>

            {/* Detected location badge — shown when we have their city */}
            {locationLabel && (
              <div className="out-of-area-location-badge">
                📍 Your location: <strong>{locationLabel}</strong>
              </div>
            )}

            {/* Role-specific messaging */}
            {userData?.role === 'provider' ? (
              <>
                <p className="out-of-area-body">
                  We are not currently onboarding caretakers in your area.
                  {locationLabel ? ` When we expand to ${locationLabel} we will reach out.` : ' When we expand to your area we will reach out.'}
                </p>
                <p className="out-of-area-body">
                  In the meantime, let us know you're interested and we'll contact
                  you as soon as caretaker spots open up in your community.
                </p>
              </>
            ) : (
              <>
                <p className="out-of-area-body">
                  We are not yet serving your area. Right now Evermark Memorial serves
                  Mahoning and Trumbull County in Northeast Ohio and Lawrence County
                  in Western Pennsylvania — but we are growing fast.
                </p>
                <p className="out-of-area-body">
                  {locationLabel
                    ? `When we expand to ${locationLabel} you will be notified by email.`
                    : 'When we expand to your area you will be notified by email.'}
                </p>
              </>
            )}

            {/* ===== REQUEST FORM (shown after button click) ===== */}
            {showForm ? (
              <div className="out-of-area-form">

                {/* City field — pre-filled, still editable */}
                <div className="auth-field">
                  <label>Your City</label>
                  <input
                    type="text"
                    placeholder="e.g. Pittsburgh"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    maxLength={100}
                  />
                </div>

                {/* State field — pre-filled, still editable */}
                <div className="auth-field">
                  <label>Your State</label>
                  <input
                    type="text"
                    placeholder="e.g. PA"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                    maxLength={50}
                  />
                </div>

                {/* Optional message */}
                <div className="auth-field">
                  <label>
                    Anything else you'd like us to know?
                    <span className="optional"> (optional)</span>
                  </label>
                  <textarea
                    className="booking-textarea"
                    placeholder="e.g. I have three family members buried at a local cemetery and would love this service..."
                    value={message}
                    onChange={(e) => {
                      if (e.target.value.length <= 500) setMessage(e.target.value);
                    }}
                    rows={4}
                    maxLength={500}
                  />
                  {/* Live character counter */}
                  <div className="char-counter">
                    <span className={message.length >= 480 ? 'char-counter-warn' : ''}>
                      {message.length}/500
                    </span>
                  </div>
                </div>

                {/* Submit error */}
                {submitError && <p className="agreement-error">{submitError}</p>}

                {/* Submit button */}
                <button
                  className="btn-primary out-of-area-btn"
                  onClick={handleSubmit}
                  disabled={submitting || !city || !state}
                >
                  {submitting ? 'Submitting...' : 'Submit My Request'}
                </button>

                {/* Cancel back to the main view */}
                <button
                  className="btn-link"
                  style={{ display: 'block', textAlign: 'center', marginTop: '10px' }}
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>

              </div>

            ) : (
              /* ===== PRIMARY CTA — opens the form ===== */
              <>
                <button
                  className="btn-primary out-of-area-btn"
                  onClick={() => setShowForm(true)}
                >
                  Request Service in My Area
                </button>

                <a href="/" className="out-of-area-home-link">
                  Go to Homepage
                </a>
              </>
            )}

          </>
        )}

      </div>
      {/* End card */}
    </div>
  );
}

export default OutOfArea;
